import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertCircle, XCircle, UserMinus } from 'lucide-react';

interface PlayerStatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

export const PlayerStatusBadge = ({ status, size = 'md' }: PlayerStatusBadgeProps) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'present':
        return {
          label: 'Present at Venue',
          variant: 'default' as const,
          icon: CheckCircle,
          className: 'bg-green-600/10 text-green-700 border-green-600/30'
        };
      case 'ready':
        return {
          label: 'Ready/Confirmed',
          variant: 'default' as const,
          icon: CheckCircle,
          className: 'bg-green-500/10 text-green-700 border-green-500/30'
        };
      case 'injured':
        return {
          label: 'Injured',
          variant: 'destructive' as const,
          icon: AlertCircle,
          className: 'bg-red-500/10 text-red-700 border-red-500/30'
        };
      case 'no_show':
        return {
          label: 'No Show',
          variant: 'destructive' as const,
          icon: XCircle,
          className: 'bg-gray-600/10 text-gray-700 border-gray-600/30'
        };
      case 'disqualified':
        return {
          label: 'Disqualified',
          variant: 'destructive' as const,
          icon: AlertCircle,
          className: 'bg-red-700/10 text-red-800 border-red-700/30'
        };
      case 'unavailable':
        return {
          label: 'Unavailable',
          variant: 'secondary' as const,
          icon: UserMinus,
          className: 'bg-gray-500/10 text-gray-600 border-gray-500/30'
        };
      case 'bench':
        return {
          label: 'On Bench',
          variant: 'outline' as const,
          icon: Clock,
          className: 'bg-blue-500/10 text-blue-700 border-blue-500/30'
        };
      case 'starter':
        return {
          label: 'Starting Lineup',
          variant: 'default' as const,
          icon: CheckCircle,
          className: 'bg-purple-500/10 text-purple-700 border-purple-500/30'
        };
      case 'substitute':
        return {
          label: 'Substitute In',
          variant: 'outline' as const,
          icon: UserMinus,
          className: 'bg-blue-600/10 text-blue-700 border-blue-600/30'
        };
      case 'ejected':
        return {
          label: 'Ejected',
          variant: 'destructive' as const,
          icon: AlertCircle,
          className: 'bg-red-800/10 text-red-900 border-red-800/30'
        };
      // Legacy status support
      case 'ready_to_play':
        return {
          label: 'Ready to Play',
          variant: 'default' as const,
          icon: CheckCircle,
          className: 'bg-green-500/10 text-green-700 border-green-500/30'
        };
      case 'not_ready':
        return {
          label: 'Not Ready',
          variant: 'secondary' as const,
          icon: Clock,
          className: 'bg-gray-500/10 text-gray-600 border-gray-500/30'
        };
      case 'substituted':
        return {
          label: 'Substituted',
          variant: 'outline' as const,
          icon: UserMinus,
          className: 'bg-blue-500/10 text-blue-700 border-blue-500/30'
        };
      case 'confirmed':
        return {
          label: 'Confirmed',
          variant: 'default' as const,
          icon: CheckCircle,
          className: 'bg-primary text-primary-foreground'
        };
      case 'pending':
        return {
          label: 'Pending',
          variant: 'outline' as const,
          icon: Clock,
          className: 'border-muted-foreground text-muted-foreground'
        };
      default:
        return {
          label: status.replace('_', ' '),
          variant: 'outline' as const,
          icon: UserMinus,
          className: 'border-muted-foreground text-muted-foreground'
        };
    }
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const badgeSize = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm';

  return (
    <Badge 
      variant={config.variant}
      className={`inline-flex items-center gap-1 ${badgeSize} ${config.className || ''} font-medium`}
    >
      <IconComponent className={iconSize} />
      {config.label}
    </Badge>
  );
};